import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  FitnessCenter,
  AccessTime,
  Bedtime,
  Favorite,
  Healing,
  DirectionsRun,
  Science,
  Grass,
  SetMeal,
  Schedule,
  Nature,
  SelfImprovement,
  OpenWith,
  Air,
  Spa,
  WaterDrop,
  Psychology,
  EmojiObjects,
  LocalFlorist,
} from '@mui/icons-material';
import { weeklyLockInService, WeeklyLockingWithTalents } from '../services/weeklyLockInService';

// Talent Icons mapping (from TalentTree.tsx)
const TALENT_ICONS: Record<string, React.ReactNode> = {
  'longevity-start': <Bedtime />, // Sleep Hygiene
  'longevity-cellular': <Favorite />, // Zone Two Cardio
  'longevity-metabolic': <FitnessCenter />, // Strength Training
  'longevity-advanced': <Healing />, // Oral Health
  'longevity-zone-five': <DirectionsRun />, // Zone Five Cardio
  'longevity-creatine': <Science />, // Creatine Supplementation
  'longevity-energy-balance': <AccessTime />, // Energy Balance
  'longevity-fiber-intake': <Grass />, // Fiber Intake
  'longevity-protein': <SetMeal />, // Protein Optimization
  'longevity-time-restricted-eating': <Schedule />, // Time Restricted Eating
  'longevity-whole-food-eating': <Nature />, // Whole Food, Nutrient-Dense Eating
  'healthspan-cardio': <SelfImprovement />, // Stability Mastery
  'healthspan-fitness': <OpenWith />, // Functional Mobility
  'healthspan-flexibility': <Air />, // Elastic Edge
  'healthspan-recovery': <Spa />, // Recovery Mastery
  'healthspan-alcohol': <WaterDrop />, // Alcohol Elimination
  'healthspan-micronutrients': <Science />, // Micronutrients Sufficiency
  'wellness-mindset': <Psychology />, // Morning Sun Seeker
  'wellness-breathwork': <Air />, // Guided Breath Work
  'wellness-social': <Favorite />, // Social Connection
  'wellness-purpose': <EmojiObjects />, // Purpose & Meaning
  'wellness-stress': <Spa />, // Emotional Regulation
  'wellness-skincare': <LocalFlorist />, // Skin and Hair Maintenance
};

// Tree colors from talent tree
const TREE_COLORS = {
  longevity: '#9c27b0',
  healthspan: '#00bcd4',  
  wellness: '#4caf50',
};

// Talent time per session mapping (from TalentTree.tsx)
const TALENT_TIME_PER_SESSION: Record<string, number> = {
  // Unified talent tree IDs with session durations
  'sleep-hygiene': 15,
  'oral-health': 25,
  'strength-training': 45,
  'zone-two-cardio': 30,
  'zone-five-cardio': 30,
  'sunseeker': 10,
  'creatine-supplementation': 10,
  'energy-balance': 45,
  'time-restricted-eating': 25,
  'whole-food-eating': 15,
  'fiber-intake': 30,
  'protein-optimization': 30,
  'stability-mastery': 60,
  'functional-mobility': 10,
  'elastic-edge': 15,
  'recovery-protocols': 30,
  'alcohol-elimination': 0, // No time allocation for elimination
  'micronutrient-optimization': 10,
  'guided-breath-work': 5,
  'social-connection': 30,
  'purpose-meaning': 10,
  'emotional-regulation': 10,
  'skin-hair-maintenance': 10,
  
  // Legacy IDs for backward compatibility
  'longevity-start': 15, // Sleep Hygiene
  'longevity-cellular': 30, // Zone Two Cardio
  'longevity-metabolic': 45, // Strength Training
  'longevity-advanced': 25, // Oral Health
  'longevity-zone-five': 30, // Zone Five Cardio
  'longevity-creatine': 10,
  'longevity-energy-balance': 45,
  'longevity-fiber-intake': 30,
  'longevity-protein': 30,
  'longevity-time-restricted-eating': 25,
  'longevity-whole-food-eating': 15,
  'healthspan-cardio': 60, // Stability Mastery
  'healthspan-fitness': 10, // Functional Mobility
  'healthspan-flexibility': 15, // Elastic Edge
  'healthspan-recovery': 30,
  'healthspan-alcohol': 0,
  'healthspan-micronutrients': 10,
  'wellness-mindset': 10, // Morning Sun Seeker
  'wellness-breathwork': 5, // Guided Breath Work
  'wellness-social': 30, // Social Connection
  'wellness-purpose': 10, // Purpose & Meaning
  'wellness-stress': 10, // Emotional Regulation
  'wellness-skincare': 10, // Skin and Hair Maintenance
};

interface ActivityReportingModalProps {
  isOpen: boolean;
  onClose: () => void;
  talentId: string;
  talentName: string;
  locking: WeeklyLockingWithTalents;
  onActivityReported: () => void;
}

interface ActivitySession {
  date: string;
  sessions: number;
  minutes: number;
  notes: string;
}

const ActivityReportingModal: React.FC<ActivityReportingModalProps> = ({
  isOpen,
  onClose,
  talentId,
  talentName,
  locking,
  onActivityReported,
}) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [sessions, setSessions] = useState(1);
  const [minutes, setMinutes] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingSessions, setExistingSessions] = useState<ActivitySession[]>([]);
  const [inputMode, setInputMode] = useState<'ranks' | 'custom'>('ranks');
  const [customMinutes, setCustomMinutes] = useState(0);
  
  // Track previous open state to detect when modal is opening
  const prevIsOpenRef = useRef(isOpen);

  // Calculate current week (Monday-Sunday) based on today's date, not database values
  const weekStart = useMemo(() => {
    const now = new Date();
    const day = now.getDay(); // 0=Sunday, 1=Monday, etc.
    const mondayDiff = day === 0 ? -6 : 1 - day; // If Sunday, go back 6 days to Monday
    const monday = new Date(now);
    monday.setDate(now.getDate() + mondayDiff);
    monday.setHours(0, 0, 0, 0);
    console.log('Activity Modal - Week starts Monday:', monday.toLocaleDateString('en-US'));
    return monday;
  }, [isOpen]);

  const weekEnd = useMemo(() => {
    const sunday = new Date(weekStart);
    sunday.setDate(weekStart.getDate() + 6); // Sunday is 6 days after Monday
    sunday.setHours(23, 59, 59, 999);
    console.log('Activity Modal - Week ends Sunday:', sunday.toLocaleDateString('en-US'));
    return sunday;
  }, [weekStart]);

  // Get today's date for highlighting
  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison
    console.log('Activity Modal - Today is:', now.toLocaleDateString('en-US', {weekday: 'long'}), now.toLocaleDateString('en-US'));
    return now;
  }, [isOpen]); // Recalculate when modal opens

  // Get tree type from talent ID
  const getTreeType = (talentId: string): 'longevity' | 'healthspan' | 'wellness' => {
    if (talentId.startsWith('longevity-')) return 'longevity';
    if (talentId.startsWith('healthspan-')) return 'healthspan';
    if (talentId.startsWith('wellness-')) return 'wellness';
    return 'wellness'; // default
  };

  const treeType = getTreeType(talentId);
  const treeColor = TREE_COLORS[treeType];
  const talentIcon = TALENT_ICONS[talentId] || <SelfImprovement />;

  // Get talent's time per session for auto-calculation
  const talentTimePerSession = TALENT_TIME_PER_SESSION[talentId] || 0;

  // Get committed sessions for this talent from locking data
  const commitmentDetails = locking.locked_talents.find(t => t.talent_id === talentId);
  const commitedSessions = commitmentDetails ? Math.round(commitmentDetails.target_minutes_per_week / talentTimePerSession) : 1;
  const commitedMinutes = commitmentDetails?.target_minutes_per_week || 0;

  // Calculate total completed minutes for this talent across the whole week
  const getTotalWeeklyProgress = () => {
    const totalMinutes = existingSessions.reduce((sum, session) => sum + session.minutes, 0);
    const totalSessions = talentTimePerSession > 0 ? Math.floor(totalMinutes / talentTimePerSession) : 0;
    return { totalMinutes, totalSessions };
  };

  const weeklyProgress = getTotalWeeklyProgress();
  const isCommitmentComplete = weeklyProgress.totalMinutes >= commitedMinutes;
  const remainingMinutes = Math.max(0, commitedMinutes - weeklyProgress.totalMinutes);
  const remainingSessions = Math.max(0, commitedSessions - weeklyProgress.totalSessions);

  // Auto-calculate minutes when sessions change
  const handleSessionsChange = useCallback((newSessions: number) => {
    // Limit sessions to remaining sessions
    const maxAllowedSessions = Math.min(newSessions, remainingSessions);
    setSessions(maxAllowedSessions);
    
    // Auto-calculate minutes based on talent's time per session
    // Use the actual time per session from talent tree (not divided by 7)
    if (talentTimePerSession > 0) {
      const autoMinutes = Math.round(maxAllowedSessions * talentTimePerSession);
      setMinutes(autoMinutes);
    }
  }, [remainingSessions, talentTimePerSession]);

  const getTreeGradient = (treeType: string) => {
    switch (treeType) {
      case 'longevity':
        return 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)';
      case 'healthspan':
        return 'linear-gradient(135deg, #00bcd4 0%, #4dd0e1 100%)';
      case 'wellness':
        return 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)';
      default:
        return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }
  };

  const loadExistingSessions = useCallback(async () => {
    try {
      const progress = await weeklyLockInService.getProgress(locking.id);
      const talentProgress = progress.filter(p => p.talent_id === talentId);
      
      const sessions: ActivitySession[] = talentProgress.map(p => ({
        date: p.entry_date,
        sessions: 1, // We don't track session count in current schema, default to 1
        minutes: p.minutes_completed,
        notes: p.notes || '',
      }));
      
      setExistingSessions(sessions);
    } catch (err) {
      console.error('Error loading existing sessions:', err);
    }
  }, [locking.id, talentId]);

  // Effect for initial setup when modal opens
  useEffect(() => {
    // Only reset form when modal is transitioning from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      // Initialize with today's date if within week range
      const todayString = today.toISOString().split('T')[0];
      const weekStartString = weekStart.toISOString().split('T')[0];
      const weekEndString = weekEnd.toISOString().split('T')[0];
      
      
      // Always try to select today first, but if today is outside the week range, select the closest day
      if (todayString >= weekStartString && todayString <= weekEndString) {
        setSelectedDate(todayString);
      } else if (todayString < weekStartString) {
        setSelectedDate(weekStartString); // Today is before the week, select week start
      } else {
        setSelectedDate(weekEndString); // Today is after the week, select week end
      }
      
      // Reset form and auto-calculate initial minutes
      setSessions(1);
      setInputMode('ranks');
      setCustomMinutes(0);
      setError(null);
      
      // Auto-calculate initial minutes for 1 session
      if (talentTimePerSession > 0) {
        const initialMinutes = Math.round(talentTimePerSession);
        setMinutes(initialMinutes);
      } else {
        setMinutes(0);
      }
    }
    
    // Update the ref for next render
    prevIsOpenRef.current = isOpen;
  }, [isOpen, talentTimePerSession, today, weekStart, weekEnd]);

  // Separate effect for loading sessions
  useEffect(() => {
    if (isOpen) {
      loadExistingSessions();
    }
  }, [isOpen, locking.id, talentId, loadExistingSessions]);

  // Additional effect to ensure date is always selected
  useEffect(() => {
    if (isOpen && !selectedDate) {
      const todayString = today.toISOString().split('T')[0];
      const weekStartString = weekStart.toISOString().split('T')[0];
      const weekEndString = weekEnd.toISOString().split('T')[0];
      
      if (todayString >= weekStartString && todayString <= weekEndString) {
        setSelectedDate(todayString);
      } else {
        setSelectedDate(weekStartString);
      }
    }
  }, [isOpen, selectedDate, today, weekStart, weekEnd]);

  // Effect to ensure proper sessions initialization after data loads
  useEffect(() => {
    if (isOpen && inputMode === 'ranks' && remainingSessions > 0 && (sessions === 0 || minutes === 0)) {
      handleSessionsChange(1);
    }
  }, [isOpen, inputMode, remainingSessions, sessions, minutes, handleSessionsChange]);

  // Effect that runs after existing sessions are loaded to initialize sessions
  useEffect(() => {
    if (isOpen && inputMode === 'ranks' && existingSessions.length >= 0 && sessions === 1 && minutes === 0 && talentTimePerSession > 0) {
      setMinutes(talentTimePerSession);
    }
  }, [isOpen, inputMode, existingSessions, sessions, minutes, talentTimePerSession]);

  const handleSubmit = async () => {
    if (!selectedDate) {
      setError('Please select a date');
      return;
    }

    const finalMinutes = inputMode === 'custom' ? customMinutes : minutes;

    if (finalMinutes <= 0) {
      setError('Minutes must be greater than 0');
      return;
    }

    // Validate that custom minutes don't exceed remaining commitment
    if (inputMode === 'custom' && customMinutes > remainingMinutes) {
      setError(`Custom minutes cannot exceed remaining commitment (${remainingMinutes} minutes)`);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Submit the activity as a progress entry
      const notes = inputMode === 'custom' 
        ? `Custom entry: ${finalMinutes} minutes`
        : `${sessions} session${sessions > 1 ? 's' : ''}, ${finalMinutes} minutes`;

      await weeklyLockInService.submitProgress(
        locking.id,
        talentId,
        finalMinutes,
        0, // We'll let the backend calculate percentage
        'app',
        undefined, // quality_rating
        notes,
        new Date(selectedDate)
      );

      onActivityReported();
    } catch (err) {
      console.error('Error submitting activity:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit activity');
    } finally {
      setSaving(false);
    }
  };

  const getWeekDays = () => {
    const days = [];
    for (let date = new Date(weekStart); date <= weekEnd; date.setDate(date.getDate() + 1)) {
      const dateString = date.toISOString().split('T')[0];
      // Standardized day format: always 3-letter day name
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      const dayNumber = date.getDate();
      // Simple date comparison - both should be in same timezone
      const dayDate = new Date(date);
      dayDate.setHours(0, 0, 0, 0);
      const isToday = dayDate.getTime() === today.getTime();
      const hasActivity = existingSessions.some(s => s.date === dateString);
      
      days.push({
        value: dateString,
        dayName,
        dayNumber,
        isToday,
        hasActivity,
      });
    }
    return days;
  };

  const getExistingActivityForDate = (date: string) => {
    return existingSessions.find(s => s.date === date);
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 2,
        background: getTreeGradient(treeType),
        color: 'white',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {talentIcon}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Report Activity
            </Typography>
            <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
              {talentName}
            </Typography>
          </Box>
        </Box>
        <IconButton 
          onClick={onClose}
          sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Completion Status Alert */}
        {isCommitmentComplete ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            🎉 Congratulations! You have already completed this activity for the week.
            <br />
            <strong>Completed:</strong> {weeklyProgress.totalSessions}/{commitedSessions} sessions ({weeklyProgress.totalMinutes}/{commitedMinutes} minutes)
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Weekly Progress:</strong> {weeklyProgress.totalSessions}/{commitedSessions} sessions completed ({weeklyProgress.totalMinutes}/{commitedMinutes} minutes)
            <br />
            <strong>Remaining:</strong> {remainingSessions} sessions ({remainingMinutes} minutes)
          </Alert>
        )}

        {/* Only show activity input if commitment is not complete */}
        {!isCommitmentComplete && (
          <>
            {/* Weekly Activity Summary */}
            {existingSessions.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Weekly Activity Summary
                </Typography>
                <Card sx={{ p: 2, bgcolor: 'transparent', border: `1px solid ${treeColor}30` }}>
                  <Grid container spacing={2}>
                    {existingSessions.map((session) => {
                      const sessionDate = new Date(session.date);
                      const dayName = sessionDate.toLocaleDateString('en-US', { weekday: 'short' });
                      const dayNumber = sessionDate.getDate();
                      
                      return (
                        <Grid item xs={12} sm={6} md={4} key={session.date}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            p: 1,
                            bgcolor: `${treeColor}15`,
                            borderRadius: 1,
                            border: `1px solid ${treeColor}30`
                          }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 35 }}>
                              {dayName} {dayNumber}:
                            </Typography>
                            <Typography variant="body2" sx={{ color: treeColor, fontWeight: 600 }}>
                              {session.minutes} minutes
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Card>
              </Box>
            )}

            {/* Week Calendar */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Select Day
              </Typography>
              <Grid container spacing={1}>
                {getWeekDays().map((day) => {
                  const existingActivity = getExistingActivityForDate(day.value);
                  const isSelected = selectedDate === day.value;
                  
                  return (
                    <Grid item xs key={day.value}>
                      <Card
                        onClick={() => setSelectedDate(day.value)}
                        sx={{
                          cursor: 'pointer',
                          border: 2,
                          borderColor: isSelected ? treeColor : 'grey.300',
                          bgcolor: `${treeColor}15`,
                          '&:hover': {
                            bgcolor: `${treeColor}25`,
                            borderColor: `${treeColor}80`,
                          },
                          transition: 'all 0.2s ease-in-out',
                          position: 'relative',
                          height: 100, // Fixed height for all cards
                        }}
                      >
                        <CardContent sx={{ 
                          p: 1.5, 
                          textAlign: 'center', 
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          '&:last-child': { pb: 1.5 }
                        }}>
                          {/* Day name and number - always same height */}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2 }}>
                              {day.dayName}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.2rem', lineHeight: 1.2 }}>
                              {day.dayNumber}
                            </Typography>
                          </Box>
                          
                          {/* Bottom section - fixed height for chips/activity */}
                          <Box sx={{ minHeight: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            {day.isToday && (
                              <Chip 
                                label="Today" 
                                size="small" 
                                sx={{ 
                                  fontSize: '0.6rem',
                                  height: 18,
                                  bgcolor: treeColor,
                                  color: 'white',
                                  '& .MuiChip-label': { px: 0.5 }
                                }}
                              />
                            )}
                            {existingActivity ? (
                              <Box sx={{ 
                                bgcolor: 'white', 
                                px: 1, 
                                py: 0.5, 
                                borderRadius: 1, 
                                border: `1px solid ${treeColor}`,
                                minWidth: 45 
                              }}>
                                <Typography variant="caption" sx={{ 
                                  fontWeight: 700, 
                                  color: treeColor, 
                                  fontSize: '0.75rem',
                                  display: 'block',
                                  lineHeight: 1.2
                                }}>
                                  {existingActivity.minutes}m
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="caption" sx={{ 
                                color: 'grey.500', 
                                fontSize: '0.65rem',
                                fontStyle: 'italic'
                              }}>
                                No activity
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>


            {/* Activity Details */}
            <Box sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
              {/* Input Mode Toggle */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <ToggleButtonGroup
                  value={inputMode}
                  exclusive
                  onChange={(e, newMode) => {
                    if (newMode !== null) {
                      setInputMode(newMode);
                      if (newMode === 'custom') {
                        // Initialize custom minutes with the current calculated value or a reasonable default
                        const initialCustomMinutes = minutes > 0 ? minutes : Math.min(talentTimePerSession, remainingMinutes);
                        setCustomMinutes(initialCustomMinutes);
                      }
                    }
                  }}
                  aria-label="input mode"
                  sx={{
                    '& .MuiToggleButton-root': {
                      px: 3,
                      py: 1,
                      textTransform: 'none',
                      '&.Mui-selected': {
                        bgcolor: `${treeColor}20`,
                        color: treeColor,
                        borderColor: treeColor,
                        '&:hover': {
                          bgcolor: `${treeColor}30`,
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="ranks" aria-label="session based">
                    By Sessions
                  </ToggleButton>
                  <ToggleButton value="custom" aria-label="custom minutes">
                    Custom Minutes
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {inputMode === 'ranks' ? (
                <>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                    Session Count
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <IconButton 
                        onClick={() => handleSessionsChange(Math.max(1, sessions - 1))}
                        sx={{ border: 1, borderColor: 'grey.300' }}
                        disabled={remainingSessions <= 0}
                      >
                        <RemoveIcon />
                      </IconButton>
                      <Typography variant="h4" sx={{ minWidth: 40, textAlign: 'center' }}>
                        {sessions}
                      </Typography>
                      <IconButton 
                        onClick={() => handleSessionsChange(sessions + 1)}
                        sx={{ border: 1, borderColor: 'grey.300' }}
                        disabled={sessions >= remainingSessions}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                      Number of sessions completed (max {remainingSessions} remaining)
                    </Typography>
                    {talentTimePerSession > 0 && (
                      <Typography variant="caption" sx={{ color: treeColor, fontWeight: 600, textAlign: 'center' }}>
                        Auto-calculated: {Math.round(sessions * talentTimePerSession)} minutes total
                      </Typography>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                    Custom Minutes
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <TextField
                      type="number"
                      value={customMinutes}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        // Limit to remaining minutes
                        setCustomMinutes(Math.min(value, remainingMinutes));
                      }}
                      inputProps={{
                        min: 0,
                        max: remainingMinutes,
                        step: 5,
                      }}
                      sx={{
                        width: 150,
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: treeColor,
                          },
                        },
                      }}
                      variant="outlined"
                      size="medium"
                      label="Minutes"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                      Enter custom minutes completed (max {remainingMinutes} remaining)
                    </Typography>
                    {customMinutes > 0 && talentTimePerSession > 0 && (
                      <Typography variant="caption" sx={{ color: treeColor, fontWeight: 600, textAlign: 'center' }}>
                        Equivalent to approximately {(customMinutes / talentTimePerSession).toFixed(1)} sessions
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} variant="outlined">
          {isCommitmentComplete ? 'Close' : 'Cancel'}
        </Button>
        {!isCommitmentComplete && (
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={
              saving || 
              !selectedDate || 
              (inputMode === 'ranks' && (minutes <= 0 || remainingSessions <= 0)) ||
              (inputMode === 'custom' && (customMinutes <= 0 || customMinutes > remainingMinutes))
            }
            sx={{
              background: getTreeGradient(treeType),
              px: 4,
            }}
          >
            {saving ? 'Reporting...' : 'Report Activity'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ActivityReportingModal; 